/**
 * Ads view-model 純函式 — 從 AdsSyncPage / AdsReportsPage / AdsAccountsPage 抽出
 * -----------------------------------------------------------------------------
 * 抽出理由(對齊 `features/ads/dateRange.ts` + `features/jobs/graphScope.ts` pattern):
 * 這幾支 derive 邏輯含巢狀三元 / CSV escape / NaN 防禦 / 多條件布林,必須能脫離
 * React page 單測。三個 page 從這裡 import,行為與原內聯版逐字等價(純重構)。
 *
 * **顏色**:這些函式不回傳顏色(顏色 token 留在各 page 的 `var(--xxx)` / `AEI_COLOR`)。
 */

import type { SyncJobStatus } from '@/types';
import type { AdsTranslate } from './errorMap';

// ============================================================================
// AdsSyncPage — 同步狀態標題(4 層巢狀三元)
// ============================================================================

/**
 * 從 sync job `status` + partial 派生標題 i18n key。
 *
 * 逐字搬自 `AdsSyncPage` 的 inline `title` 三元(R44.1):
 *   SUCCEEDED → sync.doneTitle
 *   FAILED    → isPartial ? sync.partialTitle : sync.failedTitle
 *   CANCELLED → sync.cancelledTitle
 *   其餘       → sync.progressTitle
 */
export function deriveSyncTitle(
  status: SyncJobStatus,
  isPartial: boolean,
  t: AdsTranslate,
): string {
  return status === 'SUCCEEDED'
    ? t('sync.doneTitle')
    : status === 'FAILED'
      ? isPartial
        ? t('sync.partialTitle')
        : t('sync.failedTitle')
      : status === 'CANCELLED'
        ? t('sync.cancelledTitle')
        : t('sync.progressTitle');
}

// ============================================================================
// 🗑️ R225 已刪除:`buildCsv`(+ 它專用的 `num`)
// ----------------------------------------------------------------------------
// 那支是 **production 死碼**:全 repo 只有 `syncViewModel.test.ts` 呼叫它(7 條測試),
// 真正的匯出鏈早就走 [reportsCsv.exportCsv](./pages/reportsCsv.ts)。
//
// 為什麼刪而不是留著:它是**繞過資安判準的模板**。R211 給 `exportCsv` 加了 CSV formula
// injection 中和(CWE-1236,前置 `'`);`buildCsv` 只做「逗號換空白」,**沒有那道中和**,
// 而它處理的 `campaign_name` 正是外部匯入的 Google Ads 字串(使用者可控)。一個名字叫
// `buildCsv`、有 7 條綠測試在養、看起來完全可用的函式擺在那裡,下一個要做匯出的人接上去
// 就把已修好的漏洞重新引入 —— 而且測試會全綠,因為那 7 條測試釘死的正是「沒有中和」的行為。
//
// 死碼 + 有測試在養 + 繞過現行資安判準 = 一定要刪除,不是隱藏。要再做 CSV 匯出請走
// `exportCsv`(它同時處理 BOM / CRLF / 引號跳脫 / 公式中和)。
// ============================================================================

// ============================================================================
// AdsReportsPage — AEI tier 分類(59/60 分界)
// ============================================================================

export type AeiTier = 'high' | 'mid' | 'low';

/**
 * AEI score → tier。逐字搬自 AdsReportsPage 的 `aeiTier`:
 *   >= 60 → high(60 是 high,59 是 mid 的分界,務必保留)
 *   >= 40 → mid
 *   其餘   → low
 */
export function aeiTier(score: number): AeiTier {
  if (score >= 60) return 'high';
  if (score >= 40) return 'mid';
  return 'low';
}

// ============================================================================
// AdsReportsPage — 同步時間「N 分鐘前」(NaN / 缺值回 null)
// ============================================================================

/**
 * overview `dataAsOf` timestamp → 「N 分鐘前」整數(min 0);缺值 / 非法 timestamp
 * 回 `null`(不顯假數字,pill 隱藏)。逐字搬自 AdsReportsPage 的 `syncMinutesAgo` IIFE。
 *
 * `now` 注入給測試釘死(production 端傳 `Date.now()`,行為等價)。
 */
export function syncMinutesAgo(
  raw: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!raw) return null;
  const ts = new Date(raw).getTime();
  if (Number.isNaN(ts)) return null;
  return Math.max(0, Math.round((now - ts) / 60000));
}

// ============================================================================
// AdsAccountsPage — 帳號 action state(needsReauth 三條件)
// ============================================================================

export interface AccountActionState {
  /**
   * 是否需要重新授權。三條件 OR(逐字搬自 AdsAccountsPage,R42.12;R299 第一條改正向):
   *   status !== 'ACTIVE'(BE 主判;Google 只有 `TOKEN_EXPIRED` 會落這裡,Meta 還有 `REVOKED`)
   *   syncStatus === 'TOKEN_EXPIRED'(最近一次 sync 結果)
   *   lastErrorClass === 'OAUTH_EXPIRED'
   */
  needsReauth: boolean;
}

/**
 * 從 account 三欄派生 reauth 旗標。
 *
 * ⚠️ **R263 移除了 `displaySyncStatus`**(FB-20260819-0009)。它原本是
 * `account.syncStatus ?? 'NEVER'` —— 那個 `?? 'NEVER'` 把「不知道」翻譯成「從未同步過」,
 * 而 BE 的 `sync_status` 只在建 doc 當下寫一次 `"NEVER"`、之後永不更新(`last_sync_at`
 * 更是全 repo 沒有 writer)→ 那個 fallback 對**每一顆帳戶、永遠**都成立,於是同步成功
 * 三次的帳戶照樣顯示「尚未同步過」。
 *
 * 同步狀態的推導改由 [accountSyncFacts](./accountSyncFacts.ts) 負責(它以 sync job
 * 歷史為事實來源,並保留「wire 有值就用 wire」讓 BE 補回寫後自動接手)。
 * 這裡**刻意不留**一個看起來可用的 `displaySyncStatus` —— 留著就會有人接回去。
 *
 * `needsReauth` 保留三條件不動:它問的是「要不要重新授權」,判準來自 `status` /
 * `lastErrorClass`,與同步歷史無關。
 */
export function deriveAccountActionState(account: {
  /**
   * R299 起 open `string`(Meta 端點會回 `REVOKED`)。判準改**正向** `!== 'ACTIVE'`:
   * Google 的值域只有兩個,行為逐位元不變;Meta 的 `REVOKED` 與任何未知新值一律歸
   * 「需重新授權」—— fail-safe 方向是提示重授權,不是當成還能用(同 `identityNeedsReauth`)。
   */
  status: string;
  /** wire 開放值域 → `string`;這裡只比對 `TOKEN_EXPIRED` 一個值,新值不影響判準。 */
  syncStatus: string | null;
  lastErrorClass: string | null;
}): AccountActionState {
  // R301 — 授權事實改「每個綁定各自的」(BE docs/37 §3 2026-09-07):BE 對「此綁定的身分死了」
  // 寫 `sync_status=TOKEN_EXPIRED` + `last_error_class=ADS_OAUTH_EXPIRED`(job 層則是 `NEEDS_REAUTH`)。
  // 三個 class 都是「重新授權可解」;⚠️ `ADS_CUSTOMER_NOT_ACCESSIBLE` **不在**這裡(身分活著、
  // 是帳戶端不給看 → 重授權無效),它走 `deriveConnectionState` 的 `off` 格。
  // R306 — `sync_status=TOKEN_EXPIRED` 只有在 error_class **沒有**明說「重授權無效」時才算數
  // (B12:舊 BE 用「字串含 TOKEN」判斷,把 `TOKEN_TIMEOUT_EXHAUSTED` 也寫成 `TOKEN_EXPIRED`)。
  // ⚠️ `status !== 'ACTIVE'` **不受否決** —— 那是 OAuth 層的事實(token 真的死了),與同步層無關。
  const needsReauth =
    account.status !== 'ACTIVE' ||
    (!reauthRuledOut(account.lastErrorClass) && account.syncStatus === 'TOKEN_EXPIRED') ||
    account.lastErrorClass === 'OAUTH_EXPIRED' ||
    account.lastErrorClass === 'ADS_OAUTH_EXPIRED' ||
    account.lastErrorClass === 'NEEDS_REAUTH';
  return { needsReauth };
}

/**
 * R306 — 這顆 `last_error_class` 是不是 BE 明說「**重新授權無效**」的那一族(docs/36 §0.A)。
 *
 * 現在只有一顆:`TOKEN_TIMEOUT_EXHAUSTED`(BE 解 perm token 撞 transient 重試到上界 = 基礎設施
 * 問題,使用者無事可做,系統會自動重試)。它的名字裡有 `TOKEN`,而 2026-09-10 前 BE 的
 * `_sync_status_for_failure` 是**字串包含判斷**(`"TOKEN" in error_class`)→ 帳戶卡被寫成
 * `sync_status=TOKEN_EXPIRED` → FE 顯「需要重新授權」→ **走完整條 OAuth consent 才發現沒用**
 * (beta 2026-09-08/09 Zoe 兩個帳戶的夜間排程,BE docs/115 §5 B12)。BE 已改成白名單列舉,
 * 但**這條判準要留著**:①beta / prod 還沒有 BE 修法 ②失敗方向只會少給一次重授權入口(使用者
 * 仍可從身分頁重授權),而錯的方向是讓人白跑一趟 OAuth。
 *
 * ⚠️ **不要**改成「字串含 TIMEOUT 就否決」—— 那是複製 BE 踩過的那個坑;新值一律顯式登記。
 */
export function reauthRuledOut(lastErrorClass: string | null): boolean {
  return lastErrorClass === 'TOKEN_TIMEOUT_EXHAUSTED';
}
